/**
 * Local create / update / delete helpers for team pages.
 *
 * Why this lives here instead of `@/api/profiles/teamPagesService`:
 * the shared `teamPagesService` was intentionally kept narrow (list + get +
 * updateStatus); the staff parity
 * rollout (specs/profiles/staff-ui-parity.md Group 7) needs full mutation access. Until
 * the broader profiles-forms extraction lands (Group 0b Option B), these
 * helpers wrap the raw client to avoid a wider service refactor.
 */
import * as api from "@/api/client";
import type { TeamPage } from "@/api/profiles";

const BASE = "/v1/profiles/team-pages";

export type TeamPageMutationInput = Record<string, unknown>;

/** Response shape from the profiles-api POST/PUT, which wraps under `teamPage`. */
type TeamPageEnvelope = { teamPage: TeamPage };

function isEnvelope(value: unknown): value is TeamPageEnvelope {
  return (
    typeof value === "object" && value !== null && "teamPage" in (value as Record<string, unknown>)
  );
}

function unwrap(raw: unknown): TeamPage {
  return isEnvelope(raw) ? raw.teamPage : (raw as TeamPage);
}

export async function createTeamPage(input: TeamPageMutationInput): Promise<TeamPage> {
  const raw = await api.post<unknown>(BASE, input);
  return unwrap(raw);
}

export async function updateTeamPage(id: string, input: TeamPageMutationInput): Promise<TeamPage> {
  const raw = await api.put<unknown>(`${BASE}/${id}`, input);
  return unwrap(raw);
}

export async function removeTeamPage(id: string): Promise<void> {
  await api.del<void>(`${BASE}/${id}`);
}

/** Fetch a single team page; the API wraps the entity under `teamPage`. */
export async function fetchTeamPage(id: string): Promise<TeamPage & Record<string, unknown>> {
  const raw = await api.get<unknown>(`${BASE}/${id}`);
  return unwrap(raw) as TeamPage & Record<string, unknown>;
}
